import { CdkDragDrop, moveItemInArray } from "@angular/cdk/drag-drop";
import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from "@angular/core";
import { IGoal, OkrMethodologySettings } from "@gtmhub/goals/models";
import { ItemType } from "@gtmhub/okrs/alignment-tree/models";
import { Assignee } from "@webapp/assignees/models/assignee.models";
import { Metric } from "@webapp/okrs/metrics/models/metric.models";
import { Session } from "@webapp/sessions/models/sessions.model";

@Component({
  selector: "okr-contributors",
  templateUrl: "./okr-contributors.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OkrContributorsComponent {
  @Input() public type: "goal" | "metric";
  @Input() public metric: Metric;
  @Input() public goal: IGoal;
  @Input() public isContributingMetric: boolean;
  @Input() public contributorMetric: Metric;
  @Input() public contributorGoal: IGoal;
  @Input() public methodologySettings: OkrMethodologySettings;
  @Input() public planningSession: Session;
  @Input() public coloringStrategy: string;
  @Input() public allowedActions: string[];
  @Input() public goalChildren: object[];
  @Input() public isTrialAccount: boolean;
  @Input() public isAllowedToSubmitToMarketplace: boolean;
  @Input() public sharedOkrsMethodologySettings: OkrMethodologySettings;
  @Input() public assigneesIdMap: Record<string, Assignee>;

  @Output() public readonly addChildObjective: EventEmitter<{ itemId: string; itemType: ItemType; itemSessionId: string }> = new EventEmitter();
  @Output() public readonly openMoveMetricModal: EventEmitter<{ metric: Metric }> = new EventEmitter();
  @Output() public readonly openCloneMetricModal: EventEmitter<{ metric: Metric }> = new EventEmitter();
  @Output() public readonly openDisableSyncModal: EventEmitter<{ metric: Metric }> = new EventEmitter();
  @Output() public readonly confirmDeleteMetric: EventEmitter<{ metricId: string }> = new EventEmitter();
  @Output() public readonly handleOpenCascadingForm: EventEmitter<{ metric: Metric; goalName: string }> = new EventEmitter();
  @Output() public readonly copyItemUrlToClipboard: EventEmitter<{ itemId: string; type: ItemType }> = new EventEmitter();
  @Output() public readonly reorderGoalChild: EventEmitter<{ items: object[]; item: IGoal | Metric; oldIndex: number; newIndex: number }> = new EventEmitter();
  @Output() public readonly openUpdateMetricModal: EventEmitter<string> = new EventEmitter();

  public isAllowedToUpdateMetric: boolean;

  public onCopyItemUrlToClipboard(data): void {
    this.copyItemUrlToClipboard.emit(data);
  }

  public onAddObjectiveClick(data): void {
    this.addChildObjective.emit(data);
  }

  public onHandleOpenCascadingForm(data): void {
    this.handleOpenCascadingForm.emit(data);
  }

  public onOpenMoveMetricModal(data): void {
    if (!data.metric) return;
    this.openMoveMetricModal.emit(data.metric);
  }

  public onOpenCloneMetricModal(data): void {
    if (!data.metric) return;
    this.openCloneMetricModal.emit(data.metric);
  }

  public onOpenDisableSyncModal(data): void {
    if (!data.metric) return;
    this.openDisableSyncModal.emit(data.metric);
  }

  public onConfirmDeleteMetric(data): void {
    if (!data.itemId) return;
    this.confirmDeleteMetric.emit(data);
  }

  public onOpenUpdateMetricModal(data): void {
    if (!data.metricId) return;
    this.openUpdateMetricModal.emit(data.metricId);
  }

  public drop(event: CdkDragDrop<string[]>): void {
    if (event.currentIndex === event.previousIndex) return;

    moveItemInArray(this.goalChildren, event.previousIndex, event.currentIndex);
    this.reorderGoalChild.emit({ items: this.goalChildren, item: this.goalChildren[event.currentIndex], oldIndex: event.previousIndex, newIndex: event.currentIndex });
  }

  public getAriaLabel(item: IGoal | Metric): string {
    if ("name" in item) {
      return item.name;
    } else {
      return "";
    }
  }
}
